import { apiClient } from '../utils/api';

export type PiercingType =
  | 'EAR_LOBE' | 'EAR_CARTILAGE' | 'NOSE' | 'EYEBROW' | 'LIP'
  | 'TONGUE' | 'NAVEL' | 'NIPPLE' | 'GENITAL' | 'SURFACE' | 'DERMAL';

export interface PiercingPlacement {
  area: string;
  side?: 'left' | 'right' | 'center';
  specific?: string;
}

export interface PiercingJewelry {
  type: string;
  material: string;
  gauge: string;
}

export interface PiercingTechnique {
  method: string;
  tools?: string;
}

export interface PiercingAftercare {
  products?: string;
  instructions?: string;
}

export interface PiercingRecord {
  id: string;
  actId: string;
  clientId: string;
  practitionerId: string;
  appointmentId?: string | null;
  bodyZoneId?: string | null;
  piercingTypeModelId?: string | null;
  piercingType: PiercingType;
  placement: PiercingPlacement;
  jewelry: PiercingJewelry;
  technique: PiercingTechnique;
  needleGauge: string;
  healingTime: number;
  aftercareProvided: PiercingAftercare;
  complications: string[];
  photos: unknown[];
  isHealed: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UpsertPiercingRecordData {
  bodyZoneId?: string;
  piercingTypeModelId?: string;
  piercingType?: PiercingType;
  placement?: Partial<PiercingPlacement>;
  jewelry?: Partial<PiercingJewelry>;
  technique?: Partial<PiercingTechnique>;
  needleGauge?: string;
  healingTime?: number;
  aftercareProvided?: PiercingAftercare;
  complications?: string[];
  isHealed?: boolean;
}

interface ApiResponse<T> {
  success: boolean;
  message?: string;
  errors?: string[];
  data?: T;
}

export async function getPiercingRecord(actId: string): Promise<PiercingRecord | null> {
  const response = await apiClient(`/api/planning/acts/${actId}/piercing-record`);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to fetch piercing record');
  }
  const data: ApiResponse<{ piercingRecord: PiercingRecord | null }> = await response.json();
  return data.data?.piercingRecord ?? null;
}

export async function upsertPiercingRecord(
  actId: string,
  payload: UpsertPiercingRecordData,
): Promise<PiercingRecord> {
  const response = await apiClient(`/api/planning/acts/${actId}/piercing-record`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const err: any = new Error(errorData.message || 'Failed to save piercing record');
    if (Array.isArray(errorData.errors)) err.errors = errorData.errors;
    throw err;
  }

  const data: ApiResponse<{ piercingRecord: PiercingRecord }> = await response.json();
  if (!data.success || !data.data?.piercingRecord) {
    throw new Error(data.message || 'Failed to save piercing record');
  }
  return data.data.piercingRecord;
}
